import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, CircleCheckBig } from 'lucide-react';
import { billingApi } from '../api/services';
import type { BillingProduct } from '../types/models';
import BrandMark from '../components/BrandMark';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// SignupPage — plan picker + organisation registration form. Three plan
// cards in a grid (click to select; selection drives a violet ring + soft
// glow), then a registration form that posts to billingApi.checkout and
// redirects to Stripe.
//
// The page lives on the marketing-style dark backdrop (different from the
// in-app shell), so the cards are dark glass + violet trim rather than the
// usual shadcn Card surface.
// ---------------------------------------------------------------------------

const PURPLE = '#7C3AED';
const BG_DARK = '#0A0E17';
const CARD_BG = '#121829';
const BORDER = 'rgba(255,255,255,0.08)';

function formatPrice(pence: number, currency: string): string {
  const symbol = currency.toUpperCase() === 'GBP' ? '£' : currency.toUpperCase() === 'USD' ? '$' : currency;
  return `${symbol}${Math.round(pence / 100)}`;
}

function getFeatures(plan: BillingProduct): string[] {
  const features: string[] = [];
  if (plan.aiPlanningEnabled) features.push('AI-powered daily planning');
  if (plan.transcriptProcessingEnabled) features.push('Meeting transcript analysis');
  if (plan.imageAnalysisEnabled) features.push('Image task extraction');
  if (plan.pdfExportEnabled) features.push('PDF export');
  if (plan.webhooksEnabled) features.push('Webhooks');
  if (plan.apiAccessEnabled) features.push('API access');
  if (plan.ssoEnabled) features.push('SSO / SAML');
  if (plan.customBrandingEnabled) features.push('Custom branding');
  if (plan.crossExecutiveReportingEnabled) features.push('Cross-executive reporting');
  if (plan.prioritySupportEnabled) features.push('Priority support');
  if (plan.dedicatedOnboardingEnabled) features.push('Dedicated onboarding');
  if (plan.maxExecutiveSeats > 0) {
    features.push(`Up to ${plan.maxExecutiveSeats} executive seat${plan.maxExecutiveSeats > 1 ? 's' : ''}`);
  }
  return features;
}

export default function SignupPage() {
  const [searchParams] = useSearchParams();
  const preselectedPlan = searchParams.get('plan')?.toLowerCase() || '';

  const [plans, setPlans] = useState<BillingProduct[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [orgName, setOrgName] = useState('');
  const [executiveName, setExecutiveName] = useState('');
  const [email, setEmail] = useState('');
  const [seatCount, setSeatCount] = useState(1);

  useEffect(() => {
    billingApi.getPlans()
      .then((data) => {
        setPlans(data);
        const match = data.find(p => p.name.toLowerCase().includes(preselectedPlan));
        if (match) {
          setSelectedPlanId(match.id);
        } else {
          const popular = data.find(p => p.isPopular);
          if (popular) setSelectedPlanId(popular.id);
        }
      })
      .catch(() => {
        setError('Unable to load plans. Please refresh the page.');
      })
      .finally(() => setLoading(false));
  }, [preselectedPlan]);

  const selectedPlan = useMemo(() => plans.find(p => p.id === selectedPlanId), [plans, selectedPlanId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPlanId) {
      setError('Please select a plan.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const { checkoutUrl } = await billingApi.checkout({
        productId: selectedPlanId,
        email,
        organisationName: orgName,
        executiveName,
        seatCount,
      });
      window.location.href = checkoutUrl;
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.title || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  if (submitting) {
    return (
      <div
        className="flex min-h-screen flex-col items-center justify-center"
        style={{ backgroundColor: BG_DARK }}
      >
        <Loader2 className="mb-3 h-12 w-12 animate-spin" style={{ color: PURPLE }} />
        <p className="text-base font-semibold text-white">Redirecting to secure payment...</p>
        <p className="mt-1 text-sm text-white/60">
          You will be redirected to Stripe to complete your subscription.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 py-10 md:py-16" style={{ backgroundColor: BG_DARK }}>
      <div className="mx-auto max-w-[900px]">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="inline-flex">
            <BrandMark size="md" showStratiumLine accent="light" />
          </div>
          <p className="mt-2 text-sm text-white/60">Executive orchestration platform</p>
          <h1 className="mt-8 text-2xl font-semibold tracking-tight text-white">Choose your plan</h1>
          <p className="mt-1 text-base text-white/60">
            Start with a 14-day free trial. No charge until your trial ends.
          </p>
        </div>

        {/* Plan cards */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" style={{ color: PURPLE }} />
          </div>
        ) : (
          <div className="mb-10 grid grid-cols-1 gap-4 md:grid-cols-3">
            {plans.map((plan) => {
              const isSelected = plan.id === selectedPlanId;
              const isEnterprise = plan.isCustomPricing;
              const features = getFeatures(plan);

              return (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => !isEnterprise && setSelectedPlanId(plan.id)}
                  disabled={isEnterprise}
                  className={cn(
                    'relative h-full rounded-2xl border-2 p-6 text-left transition-all',
                    isSelected
                      ? 'shadow-[0_0_24px_rgba(124,58,237,0.2)]'
                      : 'hover:border-white/15',
                    !isEnterprise && 'cursor-pointer',
                    isEnterprise && 'cursor-default',
                  )}
                  style={{
                    backgroundColor: CARD_BG,
                    borderColor: isSelected ? PURPLE : BORDER,
                  }}
                >
                  {plan.isPopular && (
                    <span
                      className="absolute right-3 top-3 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold text-white"
                      style={{ backgroundColor: PURPLE }}
                    >
                      Most popular
                    </span>
                  )}
                  <p className="mb-2 text-base font-semibold text-white">{plan.name}</p>

                  {isEnterprise ? (
                    <div className="mb-3">
                      <p className="text-2xl font-bold text-white">Custom</p>
                      <p className="text-sm text-white/60">Tailored to your organisation</p>
                    </div>
                  ) : (
                    <div className="mb-3">
                      <span className="text-3xl font-bold leading-none text-white">
                        {formatPrice(plan.pricePerSeatPence, plan.currency)}
                      </span>
                      <span className="ml-1 text-sm text-white/60">/exec/mo</span>
                    </div>
                  )}

                  {plan.description && (
                    <p className="mb-3 text-sm text-white/60">{plan.description}</p>
                  )}

                  <ul className="m-0 list-none p-0">
                    {features.map((feature) => (
                      <li key={feature} className="mb-1.5 flex items-start gap-2">
                        <CircleCheckBig
                          className="mt-0.5 h-4 w-4 shrink-0"
                          style={{ color: PURPLE }}
                        />
                        <p className="text-[13px] text-white/60">{feature}</p>
                      </li>
                    ))}
                  </ul>

                  {isEnterprise && (
                    <Button
                      asChild
                      variant="outline"
                      className="mt-3 w-full font-semibold"
                      style={{
                        borderColor: PURPLE,
                        color: PURPLE,
                        backgroundColor: 'transparent',
                      }}
                    >
                      <a href="https://www.stratiumhq.com/contact">Contact sales</a>
                    </Button>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Registration form */}
        {!loading && (
          <div className="mx-auto max-w-[480px]">
            {error && (
              <Alert variant="error" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div
              className="rounded-2xl border p-6 md:p-8"
              style={{ backgroundColor: CARD_BG, borderColor: BORDER }}
            >
              <p className="mb-6 text-center text-base font-semibold text-white">
                Create your account
              </p>

              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="org-name" className="text-white/80">Organisation Name</Label>
                  <Input
                    id="org-name"
                    required
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    className="h-11 text-white"
                    style={{ backgroundColor: BG_DARK }}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="executive-name" className="text-white/80">Your Name</Label>
                  <Input
                    id="executive-name"
                    required
                    value={executiveName}
                    onChange={(e) => setExecutiveName(e.target.value)}
                    className="h-11 text-white"
                    style={{ backgroundColor: BG_DARK }}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="signup-email" className="text-white/80">Email Address</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11 text-white"
                    style={{ backgroundColor: BG_DARK }}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="seats" className="text-white/80">Number of Executive Seats</Label>
                  <Input
                    id="seats"
                    type="number"
                    required
                    min={1}
                    max={selectedPlan?.maxExecutiveSeats || 999}
                    value={seatCount}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (val >= 1) {
                        const max = selectedPlan?.maxExecutiveSeats || 999;
                        setSeatCount(Math.min(val, max));
                      }
                    }}
                    className="h-11 text-white"
                    style={{ backgroundColor: BG_DARK }}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={!selectedPlanId || selectedPlan?.isCustomPricing}
                  size="lg"
                  className="mt-2 h-12 w-full text-base font-semibold text-white disabled:opacity-40"
                  style={{ backgroundColor: PURPLE }}
                >
                  Start 14-day free trial
                </Button>
              </form>
            </div>

            <p className="mb-2 mt-6 text-center text-[13px] text-white/60">
              14-day free trial. No charge until trial ends. Cancel anytime.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
